import { useMemo } from 'react'
import type { MenuItem } from '@/types/menu'
import type { SelectedModifier, ComboGroupSelection } from '@/types/cart'
import { rowHasDynamicPricing, calculateRowTotal } from '@/utils/ingredientPricing'

interface UsePriceCalculationReturn {
  totalPrice: number
  basePrice: number
  modifiersTotal: number
  comboGroupsTotal: number
  mainMealTotal: number
}

export function usePriceCalculation(
  item: MenuItem | null,
  quantity: number,
  selectedModifiers: SelectedModifier[],
  comboGroupSelections: ComboGroupSelection[],
  mainMealCustomizations: SelectedModifier[]
): UsePriceCalculationReturn {
  return useMemo(() => {
    if (!item) {
      return {
        totalPrice: 0,
        basePrice: 0,
        modifiersTotal: 0,
        comboGroupsTotal: 0,
        mainMealTotal: 0,
      }
    }

    let basePrice = isNaN(item.price) || item.price === null || item.price === undefined ? 0 : item.price

    // If a variation with its own price is selected, it replaces the base price
    if (item.ingredientVariationGroup) {
      const selMod = selectedModifiers.find((m) => m.groupId === item.ingredientVariationGroup!.id)
      if (selMod) {
        const selVar = item.ingredientVariationGroup.variations.find((v) => v.id === selMod.optionId)
        if (selVar?.price !== undefined) basePrice = selVar.price
      }
    }

    // For single items: add modifier prices (with dynamic free_quantity support + variation prices)
    let modifiersTotal = 0
    if (item.itemType === 'single') {
      // Group selections by row (groupId), expanding quantity entries
      const byRow = new Map<string, string[]>()
      for (const mod of selectedModifiers) {
        // Skip ingredient variation group selections from modifier total
        if (item.ingredientVariationGroup && mod.groupId === item.ingredientVariationGroup.id) continue
        const list = byRow.get(mod.groupId) || []
        const qty = mod.quantity ?? 1
        for (let i = 0; i < qty; i++) list.push(mod.optionId)
        byRow.set(mod.groupId, list)
      }
      for (const [rowId, optionIds] of byRow) {
        if (rowHasDynamicPricing(item, rowId)) {
          modifiersTotal += calculateRowTotal(item, rowId, optionIds)
        } else {
          // Static: use stored priceAdjustment (which may include variation price)
          for (const mod of selectedModifiers) {
            if (mod.groupId === rowId) {
              let adjustment = isNaN(mod.priceAdjustment) ? 0 : mod.priceAdjustment
              const qty = mod.quantity ?? 1

              // If a variation is selected, its price REPLACES the base priceAdjustment
              if (mod.variationId && item.modifierGroups) {
                const group = item.modifierGroups.find((g) => g.id === mod.groupId)
                const option = group?.options.find((o) => o.id === mod.optionId)
                const variation = option?.variationGroup?.variations.find((v) => v.id === mod.variationId)
                if (variation?.price !== undefined) {
                  adjustment = variation.price
                }
              }

              modifiersTotal += adjustment * qty
            }
          }
        }
      }
    }

    // For combo items: combo group prices
    const comboGroupsTotal =
      item.itemType === 'combo' && item.comboProduct
        ? comboGroupSelections.reduce((sum, sel) => {
            const price = isNaN(sel.additionalPrice) ? 0 : sel.additionalPrice
            return sum + price
          }, 0)
        : 0

    // For combo items: main meal customization prices (with dynamic free_quantity support)
    let mainMealTotal = 0
    if (item.itemType === 'combo' && item.comboProduct) {
      const byRow = new Map<string, string[]>()
      for (const mod of mainMealCustomizations) {
        const list = byRow.get(mod.groupId) || []
        const qty = mod.quantity ?? 1
        for (let i = 0; i < qty; i++) list.push(mod.optionId)
        byRow.set(mod.groupId, list)
      }
      for (const [rowId, optionIds] of byRow) {
        if (rowHasDynamicPricing(item, rowId)) {
          mainMealTotal += calculateRowTotal(item, rowId, optionIds)
        } else {
          for (const mod of mainMealCustomizations) {
            if (mod.groupId === rowId) {
              const adj = isNaN(mod.priceAdjustment) ? 0 : mod.priceAdjustment
              const qty = mod.quantity ?? 1
              mainMealTotal += adj * qty
            }
          }
        }
      }
    }

    const unitPrice = basePrice + modifiersTotal + comboGroupsTotal + mainMealTotal
    const totalPrice = unitPrice * quantity

    return {
      totalPrice,
      basePrice,
      modifiersTotal,
      comboGroupsTotal,
      mainMealTotal,
    }
  }, [item, quantity, selectedModifiers, comboGroupSelections, mainMealCustomizations])
}
